import { useProtocolState } from '@/hooks/useProtocolState'


/// Single-reserve counterpart to useVaultDetail, and deliberately the same contract:
/// `{ reserve, isLoading, notFound, notDeployed }`, so /markets/:asset can render the identical
/// layered guards /vaults/:address already does and an unknown address in the URL resolves to an
/// honest "not found" instead of a blank page.
///
/// **Unlike useVaultDetail this issues no reads of its own.** `useProtocolState` already fetches
/// every reserve's full config, state, incentives and price in one multicall, and wagmi dedupes
/// that across components - so a second hook here would add RPC traffic to re-fetch data already
/// in memory. It resolves an address to an entry in that set instead.
///
/// Matching is on the UNDERLYING asset address, lowercased. Not the reserve `key`: keys are
/// display slugs derived from symbols (see `slugFor`), two assets can present the same symbol, and
/// a URL should name the thing the contract names.
export function useReserveDetail(asset) {
  const { d, isLoading, deployed, refetch } = useProtocolState()

  const wanted = (asset || '').toLowerCase()
  const reserve = wanted
    ? d.reserves.find((r) => (r.asset || '').toLowerCase() === wanted)
    : undefined

  if (!deployed) return { reserve: null, isLoading: false, notFound: false, notDeployed: true, refetch, d }
  if (isLoading) return { reserve: null, isLoading: true, notFound: false, notDeployed: false, refetch, d }
  // Only meaningful once loading has finished - mid-load the list is legitimately empty, and
  // reporting "not found" then would flash a wrong answer before the right one.
  if (!reserve) return { reserve: null, isLoading: false, notFound: true, notDeployed: false, refetch, d }

  return { reserve, isLoading: false, notFound: false, notDeployed: false, refetch, d }
}

// The curve maths moved to src/lib/rateCurve.js so it can be tested without wagmi. Re-exported
// here because every consumer reaches for the hook and the curve together, and splitting the
// import across two paths at each call site buys nothing.
export { buildRateCurve, projectUtilization, ratesAtUtilization } from '@/lib/rateCurve'
